import { useState } from "react";
import MetricsBar from "../components/MetricsBar";
import InvestmentLineChart from "../components/Charts";
import TransactionTable from "../components/TransactionTable";
import PartnerOwnershipChart from "../components/PartnerOwnershipChart";
import AdminTransactions from "../components/AdminTransactions";
import PartnersManager from "../components/PartnersManager";
import ExpensesManager from "../components/ExpensesManager";
import BranchesManager from "../components/BranchesManager";
import AuditLog from "../components/AuditLog";
import MonthlySummaryManager from "../components/MonthlySummaryManager";
import EmailManager from "../components/EmailManager";
import { useResponsive } from "../lib/useResponsive";

interface DashboardPageProps {
  userEmail?: string;
}

export default function DashboardPage({ userEmail = "" }: DashboardPageProps) {
  const adminEmails: string[] = JSON.parse(import.meta.env.VITE_ADMIN_USER_EMAILS || "[]");
  const isAdmin = !!userEmail && adminEmails.map(email => email.toLowerCase()).includes(userEmail.toLowerCase());
  const [activeTab, setActiveTab] = useState<"overall" | "user" | "transactions" | "partners" | "expenses" | "branches" | "audit" | "monthly" | "email">("user");
  const showUserOnly = !isAdmin || activeTab !== "overall";
  const adminTab = activeTab === "expenses" ||
    (isAdmin && (activeTab === "transactions" || activeTab === "partners" || activeTab === "branches" || activeTab === "audit" || activeTab === "monthly" || activeTab === "email"));
  const { isMobile } = useResponsive();

  return (
    <div style={S.page}>
      <div style={{
        ...S.topHeaderContainer,
        padding: isMobile ? "16px 16px 0px" : "24px 24px 0px",
      }}>
        <div style={{
            ...S.tabContainer,
            gap: isMobile ? 8 : 12,
          }}>
            {isAdmin && (
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "overall" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("overall")}
            >
              Overall View
            </button>
            )}
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "user" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("user")}
            >
              {isAdmin ? "User View" : "My Portfolio"}
            </button>
            {isAdmin && (<>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "transactions" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("transactions")}
            >
              Transactions
            </button>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "partners" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("partners")}
            >
              Partners
            </button>
            </>)}
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "expenses" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("expenses")}
            >
              Expenses
            </button>
            {isAdmin && (<>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "branches" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("branches")}
            >
              Branches
            </button>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "monthly" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("monthly")}
            >
              Monthly NAV
            </button>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "audit" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("audit")}
            >
              Audit
            </button>
            <button
              style={{
                ...S.tabButton,
                ...(activeTab === "email" ? S.activeTab : {}),
                fontSize: isMobile ? 13 : 14,
                padding: isMobile ? "6px 12px" : "8px 16px",
              }}
              onClick={() => setActiveTab("email")}
            >
              Email
            </button>
            </>)}
          </div>
      </div>

      {adminTab ? (
        <div style={{
          ...S.container,
          padding: isMobile ? "20px 16px 60px" : "40px 24px 80px",
        }}>
          {activeTab === "transactions" && <AdminTransactions />}
          {activeTab === "partners" && <PartnersManager />}
          {activeTab === "expenses" && <ExpensesManager isAdmin={isAdmin} />}
          {activeTab === "branches" && <BranchesManager />}
          {activeTab === "audit" && <AuditLog />}
          {activeTab === "monthly" && <MonthlySummaryManager />}
          {activeTab === "email" && isAdmin && <EmailManager />}
        </div>
      ) : (
      <>
      <MetricsBar showUserOnly={showUserOnly} />

      <div style={{
        ...S.container,
        padding: isMobile ? "20px 16px 60px" : "40px 24px 80px",
        gap: isMobile ? 16 : 24,
      }}>
        <div style={S.sectionLabel}>
          <span style={S.labelLine} />
          <span style={S.labelText}>
            {showUserOnly ? "MY PORTFOLIO OVERVIEW" : "SYSTEM OVERVIEW"}
          </span>
          <span style={S.labelLine} />
        </div>

        <div style={S.fullCol}>
          <InvestmentLineChart showUserOnly={showUserOnly} />
        </div>

        {isAdmin && activeTab === "overall" && (
          <div style={S.fullCol}>
            <PartnerOwnershipChart />
          </div>
        )}

        <TransactionTable showUserOnly={showUserOnly} />
      </div>
      </>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  page: { background: "var(--dark-bg)", minHeight: "100vh" },
  topHeaderContainer: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "24px 24px 0px",
  },
  container: {
    maxWidth: 1200, margin: "0 auto",
    padding: "40px 24px 80px",
    display: "flex", flexDirection: "column", gap: 24,
  },
  sectionLabel: {
    display: "flex", alignItems: "center", gap: 16, marginTop: 8,
  },
  labelLine: { flex: 1, height: 1, background: "rgba(255,255,255,0.07)" },
  labelText: {
    fontSize: 10, letterSpacing: "0.2em", color: "var(--text-muted)",
    fontWeight: 500, whiteSpace: "nowrap",
  },
  fullCol: { width: "100%" },
  tabContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    paddingBottom: 12,
  },
  tabButton: {
    background: "none",
    border: "none",
    color: "var(--text-muted)",
    cursor: "pointer",
    fontSize: 14,
    fontWeight: 500,
    padding: "8px 16px",
    borderRadius: 4,
    transition: "all 0.2s ease",
  },
  activeTab: {
    background: "rgba(255, 255, 255, 0.1)",
    color: "#ffffff",
  },
};
